import { useQuery } from '@tanstack/react-query';
import { ActionStack, Alert, Badge, Button, Card, CheckboxField, DataList, TextField } from '@rooming/ui';
import { useState } from 'react';
import { backoffice, type HotelAdmin, type Hotel } from '../api';
import { Loaded, ReasonDialog, useCommand } from '../common';

// Hotel administrators (ADR-0013): created and scoped by the super administrator. Each one operates only the hotels
// chosen here, and at least one is mandatory.

/** The hotels as a checklist; `value` is the set of chosen ids. */
function HotelPicker({
  hotels,
  value,
  onChange,
}: {
  readonly hotels: readonly Hotel[];
  readonly value: readonly string[];
  readonly onChange: (next: string[]) => void;
}) {
  const active = hotels.filter((h) => h.status === 'ACTIVE');
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium">Hoteles que administra (al menos uno)</legend>
      {active.length === 0 ? (
        <p className="text-sm text-muted">Todavía no hay hoteles activos en tu país.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {active.map((hotel) => (
            <CheckboxField
              key={hotel.id}
              checked={value.includes(hotel.id)}
              onChange={(event) => {
                onChange(event.target.checked ? [...value, hotel.id] : value.filter((id) => id !== hotel.id));
              }}
            >
              {hotel.name} · {hotel.city}
            </CheckboxField>
          ))}
        </div>
      )}
      {value.length === 0 && <p className="text-sm text-muted">Elegí al menos un hotel.</p>}
    </fieldset>
  );
}

export function HotelAdminsSection() {
  const admins = useQuery({
    queryKey: ['hotel-admins'],
    queryFn: async () => (await backoffice.hotelAdmins()).items,
  });
  const hotels = useQuery({ queryKey: ['hotels'], queryFn: backoffice.hotels });
  const { busy, run } = useCommand();
  const emptyForm = { email: '', displayName: '', initialPassword: '' };
  const [form, setForm] = useState(emptyForm);
  const [newHotels, setNewHotels] = useState<string[]>([]);
  const [editing, setEditing] = useState<HotelAdmin>();
  const [chosen, setChosen] = useState<string[]>([]);
  const [toggling, setToggling] = useState<HotelAdmin>();

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-xl font-semibold">Administradores de hotel</h2>
      <p className="text-sm text-muted">
        Operan igual que un administrador, pero solo en los hoteles que les asignes aquí. No pueden crear ni dar de baja
        a otros administradores ni ver la auditoría.
      </p>
      <Loaded query={admins}>
        {(items) => (
          <DataList<HotelAdmin>
            caption="Administradores de hotel"
            rows={items}
            rowKey={(u) => u.id}
            empty="Todavía no hay administradores de hotel."
            columns={[
              { header: 'Nombre', cell: (u) => u.displayName },
              { header: 'Correo', cell: (u) => u.email },
              {
                header: 'Hoteles',
                cell: (u) => (
                  <ul className="flex flex-col gap-1">
                    {u.hotels.map((hotel) => (
                      <li key={hotel.id}>
                        {hotel.name} · {hotel.city}
                      </li>
                    ))}
                  </ul>
                ),
              },
              {
                header: 'Estado',
                cell: (u) => (
                  <Badge tone={u.status === 'ACTIVE' ? 'success' : 'neutral'}>
                    {u.status === 'ACTIVE' ? 'Activo' : 'Desactivado'}
                  </Badge>
                ),
              },
              {
                header: 'Acciones',
                end: true,
                cell: (u) => (
                  <ActionStack>
                    <Button
                      variant="primary"
                      size="sm"
                      aria-label={`Cambiar los hoteles de ${u.displayName}`}
                      onClick={() => {
                        setEditing(u);
                        setChosen(u.hotels.map((h) => h.id));
                      }}
                    >
                      Hoteles
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      aria-label={`${u.status === 'ACTIVE' ? 'Desactivar' : 'Reactivar'} a ${u.displayName}`}
                      onClick={() => {
                        setToggling(u);
                      }}
                    >
                      {u.status === 'ACTIVE' ? 'Desactivar' : 'Reactivar'}
                    </Button>
                  </ActionStack>
                ),
              },
            ]}
          />
        )}
      </Loaded>

      <Card>
        <h3 className="mb-3 text-lg font-semibold">Nuevo administrador de hotel</h3>
        <form
          className="grid gap-4 md:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            void run(
              () => backoffice.createHotelAdmin({ ...form, email: form.email.trim(), hotelIds: newHotels }),
              'Administrador de hotel creado.',
              [['hotel-admins'], ['staff-users']],
            ).then((ok) => {
              if (ok) {
                setForm(emptyForm);
                setNewHotels([]);
              }
            });
          }}
        >
          <TextField
            label="Nombre"
            required
            value={form.displayName}
            onChange={(event) => {
              setForm({ ...form, displayName: event.target.value });
            }}
          />
          <TextField
            label="Correo"
            type="email"
            autoComplete="off"
            required
            value={form.email}
            onChange={(event) => {
              setForm({ ...form, email: event.target.value });
            }}
          />
          <TextField
            label="Contraseña inicial"
            type="password"
            autoComplete="new-password"
            hint="Al menos 12 caracteres. Entregala por un canal seguro. En su primer ingreso configura la verificación en dos pasos."
            required
            value={form.initialPassword}
            onChange={(event) => {
              setForm({ ...form, initialPassword: event.target.value });
            }}
          />
          <div className="md:col-span-2">
            <HotelPicker hotels={hotels.data ?? []} value={newHotels} onChange={setNewHotels} />
          </div>
          <div className="md:col-span-2">
            <Button
              type="submit"
              loading={busy}
              disabled={
                newHotels.length === 0 ||
                form.email.trim() === '' ||
                form.displayName.trim() === '' ||
                form.initialPassword.length < 12
              }
            >
              Crear administrador de hotel
            </Button>
          </div>
        </form>
      </Card>

      <ReasonDialog
        open={editing !== undefined}
        title={editing === undefined ? '' : `Hoteles de ${editing.displayName}`}
        confirmLabel="Guardar hoteles"
        busy={busy}
        disabled={chosen.length === 0}
        onClose={() => {
          setEditing(undefined);
        }}
        onConfirm={(reason) => {
          if (editing === undefined) return;
          void run(() => backoffice.setHotelAdminHotels(editing.id, chosen, reason), 'Hoteles actualizados.', [
            ['hotel-admins'],
          ]).then((ok) => {
            if (ok) setEditing(undefined);
          });
        }}
      >
        <p className="mb-3 text-sm text-muted">
          El cambio vale desde su próxima acción. Siempre debe quedar al menos un hotel.
        </p>
        <HotelPicker hotels={hotels.data ?? []} value={chosen} onChange={setChosen} />
      </ReasonDialog>

      <ReasonDialog
        open={toggling !== undefined}
        title={
          toggling === undefined
            ? ''
            : `${toggling.status === 'ACTIVE' ? 'Desactivar' : 'Reactivar'} a ${toggling.displayName}`
        }
        confirmLabel={toggling?.status === 'ACTIVE' ? 'Desactivar' : 'Reactivar'}
        danger={toggling?.status === 'ACTIVE'}
        busy={busy}
        onClose={() => {
          setToggling(undefined);
        }}
        onConfirm={(reason) => {
          if (toggling === undefined) return;
          void run(
            () =>
              backoffice.setHotelAdminStatus(toggling.id, toggling.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE', reason),
            toggling.status === 'ACTIVE' ? 'Administrador desactivado.' : 'Administrador reactivado.',
            [['hotel-admins']],
          ).then((ok) => {
            if (ok) setToggling(undefined);
          });
        }}
      >
        {toggling?.status === 'ACTIVE' ? (
          <Alert tone="warning" title="Se cierran sus sesiones">
            Pierde el acceso al instante. Sus hoteles se conservan por si lo reactivás.
          </Alert>
        ) : null}
      </ReasonDialog>
    </section>
  );
}
